import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { companyAPI } from '../services/api';
import Layout from '../components/Layout';
import AdminBikes from '../components/AdminBikes';
import AdminServices from '../components/AdminServices';
import AdminUsers from '../components/AdminUsers';
import EnterpriseLockedOverlay from '../components/EnterpriseLockedOverlay';
import Icon from '../components/Icon';
import { getTabPillStateStyles } from '../utils/themeHelpers';

const SquareInventoryManager = lazy(() => import('../components/SquareInventoryManager'));

function AdminManagementPage({ user, onLogout, darkMode = true, onToggleDarkMode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'bikes');
  const [companyInfo, setCompanyInfo] = useState(null);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await companyAPI.getCompanyInfo();
        setCompanyInfo(res.data?.company || null);
      } catch {
        setCompanyInfo(null);
      }
    };
    fetchCompany();
  }, []);

  // Sync tab with URL parameter
  useEffect(() => {
    if (tabFromUrl && ['bikes', 'services', 'users', 'inventory'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  const tier = (companyInfo?.subscription_tier || '').toLowerCase();
  const showInventoryLocked = !['enterprise', 'lifetime'].includes(tier);

  // Update URL when tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const isTechnician = user?.role === 'technician';
  const tabs = [
    { id: 'bikes', label: 'Bikes', iconName: 'bike' },
    { id: 'services', label: 'Services', iconName: 'service' },
    ...(!isTechnician ? [{ id: 'inventory', label: 'Inventory', iconName: 'inventory' }] : []),
    ...(!isTechnician ? [{ id: 'users', label: 'Users', iconName: 'profile' }] : []),
  ];

  useEffect(() => {
    const tabExists = tabs.some(tab => tab.id === activeTab);
    if (!tabExists && tabs.length > 0) {
      handleTabChange(tabs[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs.map(tab => tab.id).join(','), activeTab]);

  return (
    <Layout 
      user={user} 
      onLogout={onLogout}
      pageTitle="Management"
      pageSubtitle="Manage bikes and services"
      darkMode={darkMode} 
      onToggleDarkMode={onToggleDarkMode}
      subscriptionTier={companyInfo?.subscription_tier}
    >
      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        padding: '8px',
        background: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.03)',
        borderRadius: '16px',
        marginBottom: '24px',
        overflowX: 'auto'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            style={{
              padding: '12px 16px',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: '0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              whiteSpace: 'nowrap',
              ...getTabPillStateStyles(activeTab === tab.id, darkMode),
            }}
          >
            <Icon name={tab.iconName} color={activeTab === tab.id ? "primary" : "secondary"} darkMode={darkMode} size={16} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'bikes' && <AdminBikes user={user} onLogout={onLogout} onToggleDarkMode={onToggleDarkMode} darkMode={darkMode} />}
        {activeTab === 'services' && <AdminServices user={user} onLogout={onLogout} onToggleDarkMode={onToggleDarkMode} darkMode={darkMode} />}
        {activeTab === 'inventory' && (
          showInventoryLocked ? (
            <EnterpriseLockedOverlay
              title="Inventory"
              description="Square inventory sync and catalog management for parts and accessories."
              darkMode={darkMode}
            />
          ) : (
            <Suspense fallback={<div className="loading">Loading inventory tools...</div>}>
              <SquareInventoryManager darkMode={darkMode} />
            </Suspense>
          )
        )}
        {activeTab === 'users' && <AdminUsers darkMode={darkMode} currentUser={user} />}
      </div>
    </Layout>
  );
}

export default AdminManagementPage;
